import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SvgIcon } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import BryxColors from "../types/theme";
import BryxBox from "./BryxBox";
import BryxInput from "./BryxInput";
import Decimal from "decimal.js-light";
import BryxTypography from "./BryxTypography";
const BryxNumberInput = styled((props) => {
    const { value, onChange, maxMagnitude, width, label, errorText, error, decimal, endText, ...rest } = props;
    const [lastV, setLastV] = useState(null);
    // This checks if the value is changed through props directly and updates the lastV accordingly
    useEffect(() => {
        if (value != lastV) {
            setLastV(value?.toString() ?? "");
        }
    }, [value]);
    const safeParseInt = (str) => {
        const n = Number.parseInt(str);
        if (Number.isNaN(n)) {
            return null;
        }
        return n;
    };
    const safeParseFloat = (str) => {
        const regex = /^-?\d*\.?\d+$/;
        if (!regex.test(str)) {
            return null;
        }
        const num = parseFloat(str);
        return isNaN(num) ? null : num;
    };
    const isValidDecimalString = (str) => {
        const regex = /^-?\d*\.?\d*$/;
        return regex.test(str);
    };
    const isStrIntLike = (s) => (!!props.allowNegative ? /^[0-9]+$/ : /^-?[0-9]+$/).test(s);
    const handleOnChange = (v) => {
        let newNum = null;
        if (props.decimal) {
            // Decimals get weird at large digits
            if (v.length > 14) {
                setLastV(v.slice(0, 14));
                return;
            }
            // Makes sure that it is a valid Float
            else if (Math.abs(safeParseFloat(v) ?? 0) > (props.maxMagnitude ?? 999)) {
                newNum = props.value;
                props.onChange(props.value);
                const cutOffString = v[v.length - 1] == "." ? props.value?.toString() + "." : props.value?.toString() || "";
                setLastV(cutOffString);
                return;
            }
            else if (safeParseFloat(v) != null)
                newNum = safeParseFloat(v);
            else if (v != "" && v != "-")
                newNum = props.value;
        }
        else if (Math.abs(safeParseInt(v) ?? 0) > (props.maxMagnitude ?? 999))
            newNum = props.value;
        else if (isStrIntLike(v))
            newNum = safeParseInt(v);
        else if (v != "" && v != "-")
            newNum = props.value;
        if (!props.allowNegative && newNum != null && newNum < 0)
            return;
        props.onChange(newNum);
        setLastV(v);
    };
    const onUpArrowClick = () => {
        if (Math.abs((props.value ?? 0) + 1) > (props.maxMagnitude ?? 999) || props.disabled)
            return;
        props.onChange((props.value ?? 0) + 1);
        //All of this is to make sure that the decimal places are preserved
        const tempString = new Decimal(props.value ?? 0)
            .plus(1)
            .toFixed(new Decimal(props.value ?? 0).dp())
            .toString();
        setLastV(tempString);
    };
    const onDownArrowClick = () => {
        if (Math.abs((props.value ?? 0) - 1) > (props.maxMagnitude ?? 999) || props.disabled)
            return;
        if ((props.value ?? 0) - 1 < 0 && !props.allowNegative)
            return;
        props.onChange((props.value ?? 0) - 1);
        //All of this is to make sure that the decimal places are preserved
        const tempString = new Decimal(props.value ?? 0)
            .minus(1)
            .toFixed(new Decimal(props.value ?? 0).dp())
            .toString();
        setLastV(tempString);
    };
    const parseInputValue = () => {
        if (props.decimal && isValidDecimalString(lastV ?? "") && lastV != null)
            return lastV;
        if (lastV == "-" && props.value == null)
            return props.allowNegative ? "-" : "";
        if (typeof props.value == "number")
            return props.value.toString();
        return "";
    };
    return (_jsx(BryxInput, { ...rest, value: props.value ?? "", sx: { width: `${props.width}px`, ...props.sx, transition: "200ms", ".MuiInputBase-root": { height: "100%" } }, error: props.error, errorText: props.errorText, placeholder: props.placeholder, label: props.label, onChange: (e) => handleOnChange(e.target.value), innerHeight: "46px", InputProps: {
            value: parseInputValue(),
            endAdornment: (_jsx(_Fragment, { children: props.endText ? (_jsx(BryxTypography, { variant: "body1", children: props.endText })) : (_jsxs(BryxBox, { isFlex: true, fullHeight: true, column: true, centerJustifyContent: true, children: [_jsx(SvgIcon, { viewBox: "0 0 8 5", sx: {
                                "&:hover": { color: BryxColors.lightBrand(0), cursor: "pointer" },
                                width: "8px",
                                height: "9px",
                                paddingBottom: "4px",
                            }, onClick: onUpArrowClick, children: _jsx("path", { d: "M1.22595 4.7779 L4.00358 1.83181L6.78121 4.7779C7.0604 5.07403 7.51141 5.07403 7.7906 4.7779C8.0698 4.48178 8.0698 4.00342 7.7906 3.70729L4.5047 0.222096C4.2255 -0.0740318 3.7745 -0.0740318 3.4953 0.222096L0.209396 3.70729C-0.0697987 4.00342 -0.0697987 4.48178 0.209396 4.7779C0.488591 5.06644 0.946756 5.07403 1.22595 4.7779Z" }) }), _jsx(SvgIcon, { viewBox: "0 0 8 5", sx: {
                                "&:hover": { color: BryxColors.lightBrand(0), cursor: "pointer" },
                                width: "8px",
                                height: "9px",
                                paddingTop: "4px",
                            }, onClick: onDownArrowClick, children: _jsx("path", { d: "M 1.226 0.2221 L 4.0036 3.1682 L 6.7812 0.2221 C 7.0604 -0.074 7.5114 -0.074 7.7906 0.2221 C 8.0698 0.5182 8.0698 0.9966 7.7906 1.2927 L 4.5047 4.7779 C 4.2255 5.074 3.7745 5.074 3.4953 4.7779 L 0.2094 1.2927 C -0.0698 0.9966 -0.0698 0.5182 0.2094 0.2221 C 0.4886 -0.0664 0.9468 -0.074 1.226 0.2221 Z" }) })] })) })),
        } }));
})(({ disabled }) => {
    if (disabled)
        return {
            "*": {
                cursor: "not-allowed!important",
                color: BryxColors.lightNotify("inactive"),
                borderColor: BryxColors.lightNotify("inactive") + "!important",
                "& input::placeholder": {
                    opacity: "1",
                    color: BryxColors.lightNotify("inactive"),
                },
            },
        };
    return {};
});
export default BryxNumberInput;
